<template>
  <v-container fluid>
    <v-layout row wrap>
      <template v-if="loading">
        <v-flex xs12 sm6 md4 v-for="n in 6" :key="'skeleton-' + n">
          <skeleton-card></skeleton-card>
        </v-flex>
      </template>
      <template v-else>
        <v-flex xs12 sm6 md4 v-for="kpi in kpiCards" :key="kpi.title">
          <v-card class="elevation-1">
            <v-card-text class="pa-2">
              <div class="caption grey--text">{{ kpi.title }}</div>
              <div class="title font-weight-bold mt-1 primary--text">{{ kpi.value }}</div>
              <div class="caption grey--text mt-1">{{ kpi.subtitle }}</div>
            </v-card-text>
          </v-card>
        </v-flex>
      </template>

      <v-flex xs12>
        <v-layout row wrap mb-2 class="mt-3" align-center>
          <v-flex xs12 sm4>
            <h3>Top Clients by Volume</h3>
          </v-flex>
          <v-flex xs12 sm4 v-if="!loading">
            <v-text-field
              v-model="searchQuery"
              append-icon="fa-search"
              label="Search merchants"
              single-line
              hide-details
              style="max-width: 300px;"
            ></v-text-field>
          </v-flex>
          <v-flex xs12 sm4 class="text-xs-right" v-if="!loading">
            <v-btn small color="primary" @click="exportRankings">
              <v-icon left small>fa-download</v-icon>
              Export CSV
            </v-btn>
          </v-flex>
        </v-layout>
        <skeleton-table v-if="loading" :row-count="10" :column-count="9" show-header></skeleton-table>
        <v-data-table
          v-else
          :headers="rankingHeaders"
          :items="filteredRankings"
          :pagination.sync="tablePagination"
          :rows-per-page-items="[10, 25, 50, 100, 200]"
          class="elevation-1"
        >
          <template v-slot:items="props">
            <td class="pa-1 ma-0">{{ props.item.merchant_name }}</td>
            <td class="text-xs-center pa-1 ma-0">{{ props.item.current_rank }}</td>
            <td class="text-xs-center pa-1 ma-0">
              <span v-if="getRankChange(props.item) > 0" class="rank-change rank-up">
                <v-icon small color="success">fa-arrow-up</v-icon>
                <span class="ml-1">{{ getRankChange(props.item) }}</span>
              </span>
              <span v-else-if="getRankChange(props.item) < 0" class="rank-change rank-down">
                <v-icon small color="error">fa-arrow-down</v-icon>
                <span class="ml-1">{{ Math.abs(getRankChange(props.item)) }}</span>
              </span>
              <span v-else class="rank-change rank-same">
                <v-icon small color="grey">fa-minus</v-icon>
              </span>
            </td>
            <td class="text-xs-right pa-1 ma-0">{{ formatCurrency(props.item.total_volume) }}</td>
            <td class="text-xs-right pa-1 ma-0">{{ formatCurrency(props.item.card_volume) }}</td>
            <td class="text-xs-right pa-1 ma-0">{{ formatCurrency(props.item.bank_volume) }}</td>
            <td class="text-xs-right pa-1 ma-0">{{ formatNumber(props.item.transaction_count) }}</td>
            <td class="text-xs-right pa-1 ma-0">{{ formatNumber(props.item.card_transaction_count) }}</td>
            <td class="text-xs-right pa-1 ma-0">{{ formatNumber(props.item.bank_transaction_count) }}</td>
          </template>
          <template v-slot:no-data>
            No merchant data available
          </template>
        </v-data-table>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<script>
import SkeletonCard from '@/components/common/SkeletonCard'
import SkeletonTable from '@/components/common/SkeletonTable'

export default {
  components: {
    SkeletonCard,
    SkeletonTable
  },

  props: {
    metrics: {
      type: Object,
      default: null
    },
    rankings: {
      type: [Array, Object],
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    }
  },

  data () {
    return {
      searchQuery: '',
      currentPage: 1,
      itemsPerPage: 10,
      totalItems: 0,
      sortBy: null,
      sortDescending: false,
      rankingHeaders: [
        { text: 'Merchant Name', value: 'merchant_name', sortable: true },
        { text: 'Rank', value: 'current_rank', align: 'center', sortable: false, width: '80px' },
        { text: 'Change', value: 'change', align: 'center', sortable: false, width: '100px' },
        { text: 'Total Volume', value: 'total_volume', align: 'right', sortable: true },
        { text: 'Card Volume', value: 'card_volume', align: 'right', sortable: true },
        { text: 'ACH Volume', value: 'bank_volume', align: 'right', sortable: true },
        { text: 'Transactions', value: 'transaction_count', align: 'right', sortable: true },
        { text: 'Card Txns', value: 'card_transaction_count', align: 'right', sortable: true },
        { text: 'ACH Txns', value: 'bank_transaction_count', align: 'right', sortable: true }
      ]
    }
  },

  computed: {
    kpiCards () {
      if (!this.metrics) {
        return this.getEmptyKpiCards()
      }

      return [
        {
          title: 'Total Volume',
          value: this.formatCurrency(this.metrics.total_volume),
          subtitle: `${this.formatNumber(this.metrics.total_transactions)} transactions`
        },
        {
          title: 'Card Volume',
          value: this.formatCurrency(this.metrics.card_volume),
          subtitle: `${this.formatNumber(this.metrics.card_transaction_count)} transactions`
        },
        {
          title: 'ACH Volume',
          value: this.formatCurrency(this.metrics.bank_volume),
          subtitle: `${this.formatNumber(this.metrics.bank_transaction_count)} transactions`
        },
        {
          title: 'Total Transactions',
          value: this.formatNumber(this.metrics.total_transactions),
          subtitle: `Card: ${this.formatNumber(this.metrics.card_transaction_count)} | ACH: ${this.formatNumber(this.metrics.bank_transaction_count)}`
        },
        {
          title: 'Avg Transaction',
          value: this.formatCurrency(this.metrics.avg_transaction_value),
          subtitle: this.getAverageTransactionSubtitle()
        },
        {
          title: 'Total Fees',
          value: this.formatCurrency(this.metrics.total_fees),
          subtitle: `${this.calculateFeePercentage()}% of volume`
        }
      ]
    },

    filteredRankings () {
      const rankingsData = Array.isArray(this.rankings) ? this.rankings : (this.rankings.data || [])

      if (!this.searchQuery) {
        return rankingsData
      }

      const query = this.searchQuery.toLowerCase()
      return rankingsData.filter(ranking =>
        ranking.merchant_name.toLowerCase().includes(query)
      )
    },

    tablePagination: {
      get () {
        return {
          page: this.currentPage,
          rowsPerPage: this.itemsPerPage,
          totalItems: this.totalItems,
          sortBy: this.sortBy,
          descending: this.sortDescending
        }
      },
      set (val) {
        const paginationChanged = val.rowsPerPage !== this.itemsPerPage || val.page !== this.currentPage
        const sortChanged = val.sortBy !== this.sortBy || val.descending !== this.sortDescending

        if (paginationChanged || sortChanged) {
          this.currentPage = val.page
          this.itemsPerPage = val.rowsPerPage
          this.sortBy = val.sortBy || null
          this.sortDescending = val.descending || false
          this.emitPaginationChange()
        }
      }
    }
  },

  watch: {
    rankings: {
      immediate: true,
      handler (newValue) {
        if (newValue && newValue.pagination) {
          this.totalItems = newValue.pagination.total_count
          this.itemsPerPage = newValue.pagination.limit
          this.currentPage = Math.floor(newValue.pagination.offset / newValue.pagination.limit) + 1
        } else if (Array.isArray(newValue)) {
          this.totalItems = newValue.length
        }
      }
    }
  },

  methods: {
    getAverageTransactionSubtitle () {
      if (!this.metrics) return 'N/A'

      const parts = []

      if (this.metrics.card_transaction_count > 0) {
        const cardAvg = this.metrics.card_volume / this.metrics.card_transaction_count
        parts.push(`Card: ${this.formatCurrency(cardAvg)}`)
      } else {
        parts.push('Card: N/A')
      }

      if (this.metrics.bank_transaction_count > 0) {
        const achAvg = this.metrics.bank_volume / this.metrics.bank_transaction_count
        parts.push(`ACH: ${this.formatCurrency(achAvg)}`)
      } else {
        parts.push('ACH: N/A')
      }

      return parts.join(' | ')
    },

    getRankChange (item) {
      if (!item.previous_rank) return 0
      return item.previous_rank - item.current_rank
    },

    formatCurrency (value) {
      if (typeof value === 'string') {
        value = parseFloat(value)
      }
      if (isNaN(value)) {
        return '$0.00'
      }
      return value.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })
    },

    formatNumber (value) {
      if (typeof value === 'string') {
        value = parseFloat(value)
      }
      if (isNaN(value)) {
        return '0'
      }
      return value.toLocaleString('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      })
    },

    calculateFeePercentage () {
      if (!this.metrics || this.metrics.total_volume === 0) {
        return '0.00'
      }
      const percentage = (this.metrics.total_fees / this.metrics.total_volume) * 100
      return percentage.toFixed(2)
    },

    getEmptyKpiCards () {
      return [
        {
          title: 'Total Volume',
          value: '$0.00',
          subtitle: '0 transactions'
        },
        {
          title: 'Card Volume',
          value: '$0.00',
          subtitle: '0 transactions'
        },
        {
          title: 'ACH Volume',
          value: '$0.00',
          subtitle: '0 transactions'
        },
        {
          title: 'Total Transactions',
          value: '0',
          subtitle: '0 batches'
        },
        {
          title: 'Avg Transaction',
          value: '$0.00',
          subtitle: 'Card: $0.00'
        },
        {
          title: 'Total Fees',
          value: '$0.00',
          subtitle: '0.00% of volume'
        }
      ]
    },

    emitPaginationChange () {
      const offset = (this.currentPage - 1) * this.itemsPerPage

      this.$emit('rankings-pagination-change', {
        limit: this.itemsPerPage,
        offset: offset,
        sortBy: this.sortBy || null,
        sortOrder: this.sortDescending ? 'desc' : 'asc'
      })
    },

    exportRankings () {
      const rankings = this.filteredRankings

      if (rankings.length === 0) {
        return
      }

      const headers = [
        'Rank',
        'Merchant Name',
        'Total Volume',
        'Card Volume',
        'ACH Volume',
        'Total Transactions',
        'Card Transactions',
        'ACH Transactions'
      ]

      const rows = rankings.map(ranking => [
        ranking.current_rank,
        ranking.merchant_name,
        this.formatCurrency(ranking.total_volume),
        this.formatCurrency(ranking.card_volume),
        this.formatCurrency(ranking.bank_volume),
        ranking.transaction_count,
        ranking.card_transaction_count,
        ranking.bank_transaction_count
      ])

      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(cell => {
          const cellStr = String(cell)
          const escaped = cellStr.replace(/"/g, '""')
          return `"${escaped}"`
        }).join(','))
      ].join('\n')

      const blob = new Blob([csvContent], { type: 'text/csv' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      const fileName = `top_clients_by_volume_${new Date().toISOString().split('T')[0]}.csv`
      link.setAttribute('download', fileName)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    }
  }
}
</script>

<style scoped>
.rank-change {
  display: inline-flex;
  align-items: center;
  font-weight: 500;
}

.rank-up {
  color: #4caf50;
}

.rank-down {
  color: #f44336;
}

.rank-same {
  color: #9e9e9e;
}
</style>



// WEBPACK FOOTER //
// src/components/manage/paynetworx/funding/DashboardView.vue